import { useCallback, useEffect, useMemo, useRef, useState } from "react";

export function useFocus<T extends HTMLElement>() {
  const [isFocused, setIsFocused] = useState(false);

  const ref = useRef<T>(null);

  const handleFocus = useCallback(() => {
    setIsFocused(true);
  }, []);

  const handleBlur = useCallback(() => {
    setIsFocused(false);
  }, []);

  const controls = useMemo(
    () => ({
      focus: handleFocus,
      blur: handleBlur,
    }),
    [handleBlur, handleFocus],
  );

  useEffect(() => {
    const node = ref.current;

    if (!node) {
      return;
    }

    node.addEventListener("focus", handleFocus);
    node.addEventListener("blur", handleBlur);

    return () => {
      node.removeEventListener("focus", handleFocus);
      node.removeEventListener("blur", handleBlur);
    };
  }, [handleBlur, handleFocus]);

  return [ref, isFocused, controls] as const;
}
